import { supabase } from '../lib/supabase';

// Auction Types
export interface Auction {
  id: string;
  product_id: string;
  start_time: string;
  end_time: string;
  starting_price: number;
  reserve_price?: number;
  current_bid: number;
  bid_increment: number;
  status: 'draft' | 'active' | 'paused' | 'ended' | 'cancelled';
  winning_bid_id?: string;
  winner_user_id?: string;
  created_at: string;
  updated_at: string;
  created_by: string;
}

export interface AuctionBid {
  id: string;
  auction_id: string;
  bidder_user_id: string;
  bid_amount: number;
  bid_time: string;
  status: 'active' | 'outbid' | 'winning' | 'won' | 'cancelled';
  created_at: string;
}

export interface CreateAuctionData {
  product_id: string;
  start_time?: string;
  end_time: string;
  starting_price: number;
  reserve_price?: number;
  bid_increment?: number;
  status?: 'draft' | 'active' | 'paused' | 'ended' | 'cancelled';
}

export interface CreateBidData {
  auction_id: string;
  bid_amount: number;
}

export interface AuctionWithProduct extends Auction {
  product: {
    id: string;
    name: string;
    description: string;
    images: string[];
    seller_id: string;
  };
  bid_count: number;
  highest_bidder?: {
    id: string;
    name: string;
  };
}

export class AuctionService {
  // Create a new auction
  static async createAuction(data: CreateAuctionData): Promise<Auction> {
    const { data: auction, error } = await supabase
      .from('auctions')
      .insert({
        ...data,
        created_by: (await supabase.auth.getUser()).data.user?.id,
        current_bid: data.starting_price,
      })
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to create auction: ${error.message}`);
    }

    return auction;
  }

  // Get auction by ID
  static async getAuctionById(id: string): Promise<AuctionWithProduct | null> {
    const { data, error } = await supabase
      .from('auctions')
      .select(`
        *,
        product:products!product_id(id, name, description, images, seller_id),
        auction_bids!auction_id(count)
      `)
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return null; // Auction not found
      }
      throw new Error(`Failed to get auction: ${error.message}`);
    }

    return {
      ...data,
      bid_count: data.auction_bids?.[0]?.count || 0,
    };
  }

  // Get active auctions
  static async getActiveAuctions(limit: number = 20, offset: number = 0): Promise<AuctionWithProduct[]> {
    const { data, error } = await supabase
      .from('auctions')
      .select(`
        *,
        product:products!product_id(id, name, description, images, seller_id),
        auction_bids!auction_id(count)
      `)
      .eq('status', 'active')
      .gt('end_time', new Date().toISOString())
      .order('end_time', { ascending: true })
      .range(offset, offset + limit - 1);

    if (error) {
      throw new Error(`Failed to get active auctions: ${error.message}`);
    }

    return data.map(auction => ({
      ...auction,
      bid_count: auction.auction_bids?.[0]?.count || 0,
    }));
  }

  // Get auctions by user ID
  static async getAuctionsByUser(userId: string): Promise<AuctionWithProduct[]> {
    const { data, error } = await supabase
      .from('auctions')
      .select(`
        *,
        product:products!product_id(id, name, description, images, seller_id),
        auction_bids!auction_id(count)
      `)
      .eq('created_by', userId)
      .order('created_at', { ascending: false });

    if (error) {
      throw new Error(`Failed to get user auctions: ${error.message}`);
    }

    return data.map(auction => ({
      ...auction,
      bid_count: auction.auction_bids?.[0]?.count || 0,
    }));
  }

  // Get auctions by product ID
  static async getAuctionsByProductId(productId: string): Promise<Auction[]> {
    const { data, error } = await supabase
      .from('auctions')
      .select('*')
      .eq('product_id', productId)
      .order('created_at', { ascending: false });

    if (error) {
      throw new Error(`Failed to get auctions for product: ${error.message}`);
    }

    return data;
  }

  // Update auction
  static async updateAuction(id: string, updates: Partial<Auction>): Promise<Auction> {
    // Guard against accidental mass-updates which cause PostgREST single-object errors
    if (!id) {
      throw new Error('Failed to update auction: invalid auction id');
    }

    // Use maybeSingle() to avoid PostgREST coercion error when RLS hides the row
    const { data, error } = await supabase
      .from('auctions')
      .update(updates)
      .eq('id', id)
      .select()
      .maybeSingle();

    if (error) {
      // If PostgREST cannot coerce to single JSON object (e.g., RLS or triggers),
      // treat this as a non-fatal selection issue and return a best-effort object
      if (error.message.includes('Cannot coerce the result to a single JSON object')) {
        return { id, ...(updates as any) } as unknown as Auction;
      }
      throw new Error(`Failed to update auction: ${error.message}`);
    }

    // If no data returned (e.g., due to RLS), return a best-effort merged object
    if (!data) {
      return { id, ...(updates as any) } as unknown as Auction;
    }

    return data as Auction;
  }

  // Create a bid
  static async createBid(data: CreateBidData): Promise<AuctionBid> {
    // 1) Create bid (critical path). If this fails, propagate error.
    const { data: bid, error } = await supabase
      .from('auction_bids')
      .insert({
        ...data,
        bidder_user_id: (await supabase.auth.getUser()).data.user?.id,
      })
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to create bid: ${error.message}`);
    }

    // 2) Best-effort: update auction.current_bid. If this fails, don't reject the whole flow.
    try {
      await this.updateAuction(data.auction_id, {
        current_bid: data.bid_amount,
      });
    } catch (e) {
      // Non-fatal: UI can compute highest bid from bids list immediately after
      console.warn('Non-fatal: failed to update auction current_bid after bid insert', e);
    }

    // 3) Best-effort: update previous bids statuses
    try {
      await supabase
        .from('auction_bids')
        .update({ status: 'outbid' })
        .eq('auction_id', data.auction_id)
        .neq('id', bid.id)
        .lt('bid_amount', data.bid_amount);
    } catch (e) {
      console.warn('Non-fatal: failed to mark previous bids as outbid', e);
    }

    // 4) Best-effort: set current bid to winning
    try {
      await supabase
        .from('auction_bids')
        .update({ status: 'winning' })
        .eq('id', bid.id);
    } catch (e) {
      console.warn('Non-fatal: failed to mark current bid as winning', e);
    }

    return bid;
  }

  // Get bids for an auction
  static async getAuctionBids(auctionId: string): Promise<AuctionBid[]> {
    const { data, error } = await supabase
      .from('auction_bids')
      .select('*')
      .eq('auction_id', auctionId)
      .order('bid_amount', { ascending: false });

    if (error) {
      throw new Error(`Failed to get auction bids: ${error.message}`);
    }

    return data;
  }

  // Get user's bids
  static async getUserBids(userId: string): Promise<AuctionBid[]> {
    const { data, error } = await supabase
      .from('auction_bids')
      .select('*')
      .eq('bidder_user_id', userId)
      .order('bid_time', { ascending: false });

    if (error) {
      throw new Error(`Failed to get user bids: ${error.message}`);
    }

    return data;
  }

  // End an auction
  static async endAuction(auctionId: string): Promise<Auction> {
    // Get the highest bid
    const { data: highestBid, error: bidError } = await supabase
      .from('auction_bids')
      .select('*')
      .eq('auction_id', auctionId)
      .eq('status', 'winning')
      .single();

    if (bidError && bidError.code !== 'PGRST116') {
      throw new Error(`Failed to get highest bid: ${bidError.message}`);
    }

    // Update auction status
    const updates: Partial<Auction> = {
      status: 'ended',
    };

    if (highestBid) {
      updates.winning_bid_id = highestBid.id;
      updates.winner_user_id = highestBid.bidder_user_id;
      
      // Update winning bid status
      await supabase
        .from('auction_bids')
        .update({ status: 'won' })
        .eq('id', highestBid.id);
    }

    return await this.updateAuction(auctionId, updates);
  }

  // Cancel an auction
  static async cancelAuction(auctionId: string): Promise<Auction> {
    // Update all bids to cancelled
    await supabase
      .from('auction_bids')
      .update({ status: 'cancelled' })
      .eq('auction_id', auctionId);

    // Update auction status
    return await this.updateAuction(auctionId, {
      status: 'cancelled',
    });
  }

  // Check if auction has ended and update status
  static async checkAndUpdateAuctionStatus(auctionId: string): Promise<Auction | null> {
    const auction = await this.getAuctionById(auctionId);
    
    if (!auction) {
      return null;
    }

    const now = new Date();
    const endTime = new Date(auction.end_time);

    if (auction.status === 'active' && now >= endTime) {
      return await this.endAuction(auctionId);
    }

    return auction;
  }

  // Get auctions ending soon
  static async getAuctionsEndingSoon(hours: number = 24): Promise<AuctionWithProduct[]> {
    const endTime = new Date();
    endTime.setHours(endTime.getHours() + hours);

    const { data, error } = await supabase
      .from('auctions')
      .select(`
        *,
        product:products!product_id(id, name, description, images, seller_id),
        auction_bids!auction_id(count)
      `)
      .eq('status', 'active')
      .lte('end_time', endTime.toISOString())
      .gt('end_time', new Date().toISOString())
      .order('end_time', { ascending: true });

    if (error) {
      throw new Error(`Failed to get auctions ending soon: ${error.message}`);
    }

    return data.map(auction => ({
      ...auction,
      bid_count: auction.auction_bids?.[0]?.count || 0,
    }));
  }
}
